<template>
	<form id="mfa-otp-form" @submit.prevent="handleSubmit" :key="stateChangeKey">
		<div class="w-100 mt-3 mb-4 text-center">
			<div class="otp-inputs">
				<input
					v-for="(_digit, idx) in length"
					:key="idx"
					v-model="otpDigits[idx]"
					:ref="'otpInput' + idx"
					type="text"
					pattern="[0-9]*"
					inputmode="numeric"
					maxlength="1"
					@input="onOTPInput(idx)"
					@keydown="onOTPKeydown($event, idx)"
					@change="$emit('resetErrors')"
					:placeholder="placeholder"
					:class="{ error: hasError }"
				/>
			</div>
			<div class="w-100 para-3 error-text" v-if="hasError || warning">
				<span v-if="hasError" class="text-bold error-message"><i></i>{{ error[0] }}</span>
				<span v-if="warning" :style="{ color: warningColor }"><br v-if="hasError" /><i></i>{{ warning }}</span>
			</div>
		</div>
		<AppButton :name="submitButtonName" class="w-100 mb-2" :disabled="otpNotFilled" type="submit" size="medium" :data-target="dataTarget">
			{{ submitButtonText }}
		</AppButton>
		<AppButton v-if="showHelpButton" :name="helpButtonName" class="w-100" size="medium" type="button" link @click="openHelpPageInNewTab">
			{{ helpButtonText }}
		</AppButton>
	</form>
</template>

<script>
	export default {
		name: 'OTPInput',
		props: {
			length: {
				type: Number,
				default: 6,
			},
			placeholder: {
				type: String,
				default: '0',
			},
			error: {
				type: Array,
				default: () => [],
			},
			warning: {
				type: String,
				default: '',
			},
			warningColor: {
				type: String,
				default: '#00cfcc',
			},
			stateChangeKey: {
				type: Number,
				default: 0,
			},
			dataTarget: {
				type: String,
				default: '#mfa-configuration-modal',
			},
			submitButtonName: {
				type: String,
				default: 'mfa-validate-button',
			},
			submitButtonText: {
				type: String,
				default: 'Continue',
			},
			showHelpButton: {
				type: Boolean,
				default: true,
			},
			helpButtonName: {
				type: String,
				default: 'mfa-need-help',
			},
			helpButtonText: {
				type: String,
				default: 'Need help?',
			},
		},
		data() {
			return {
				otpDigits: Array(this.length).fill(''),
				otpNotFilled: true,
			}
		},
		computed: {
			hasError() {
				return this.error && this.error.length
			},
		},
		watch: {
			otpDigits: {
				handler(newValue) {
					this.otpNotFilled = newValue.some(d => !d)
					this.$emit('otp', newValue.join(''))
				},
				deep: true,
			},
		},
		methods: {
			onOTPInput(idx) {
				// Move to next input if current input has a value and not last input
				if (this.otpDigits[idx].length > 0 && idx < this.otpDigits.length - 1) {
					const nextInput = this.$refs[`otpInput${idx + 1}`]
					if (nextInput && nextInput[0]) {
						nextInput[0].focus()
						// Select the existing content if any
						nextInput[0].select()
					}
				}
			},
			openHelpPageInNewTab() {
				window.open('https://help.stan.store/article/410-2fa-help', '_blank')
			},
			onOTPKeydown(event, idx) {
				// Handle backspace
				if (event.key === 'Backspace') {
					if (this.otpDigits[idx].length === 0 && idx > 0) {
						// If current input is empty, move to previous input
						const prevInput = this.$refs[`otpInput${idx - 1}`]
						if (prevInput && prevInput[0]) {
							prevInput[0].focus()
							prevInput[0].select()
						}
					} else {
						// If current input has value, clear it but stay on current input
						this.otpDigits[idx] = ''
					}
				}
				// Handle left arrow
				else if (event.key === 'ArrowLeft' && idx > 0) {
					const prevInput = this.$refs[`otpInput${idx - 1}`]
					if (prevInput && prevInput[0]) {
						prevInput[0].focus()
						prevInput[0].select()
					}
				}
				// Handle right arrow
				else if (event.key === 'ArrowRight' && idx < this.otpDigits.length - 1) {
					const nextInput = this.$refs[`otpInput${idx + 1}`]
					if (nextInput && nextInput[0]) {
						nextInput[0].focus()
						nextInput[0].select()
					}
				}
			},
			handleSubmit() {
				this.$emit('submit')
			},
		},
	}
</script>

<style lang="scss" scoped>
	.otp-inputs {
		display: flex;
		justify-content: space-between;
		width: 95%;
		margin: 0px auto;

		@media (max-width: 767px) {
			max-width: 250px;
		}

		input {
			width: 55px;
			height: 70px;
			font-size: 40px;
			font-weight: 600;
			color: var(--stan-primary-primary-color);
			text-align: center;
			border: 1.5px solid var(--stan-primary-soft-color);
			border-radius: 8px;
			outline: none;
			transition: border-color 0.3s;
			@media (max-width: 767px) {
				width: 35px;
				height: 45px;
				font-size: 25px;
				// Override default style sheet onm safari that adds ~15px padding on the x axis
				padding: 1px 2px 1px 2px;
			}
		}

		input:focus {
			box-shadow: 0px 0px 0px 4px var(--stan-primary-soft-color), 0px 0px 0px 4px var(--stan-primary-soft-color) !important;
		}
	}

	.error-text {
		font-size: 13px;
		font-style: normal;
		line-height: normal;
		letter-spacing: -0.26px;
		padding-top: 6px;
		padding-left: 6px;
	}

	.error {
		color: var(--stan-text-dark-color) !important;
		background-color: var(--stan-negative-light-color) !important;
	}

	.error-message {
		color: var(--stan-text-negative-color) !important;
	}
</style>
